<template>
    <div class="mapping_step">
        <v-tabs v-model="activeSection" color="indigo-darken-3" class="section_tabs">
            <v-tab v-for="(section, index) in sections" :key="section.key" :value="index">
                {{ section.title }}
            </v-tab>
        </v-tabs>

        <div class="section_description">
            <p class="section_summary">{{ currentSection.description }}</p>
            <p class="section_hint">
                Asigna cada campo a una columna del archivo. Las columnas en gris ya están asignadas a otro campo.
                Todos los campos de carga deben estar mapeados para continuar.
            </p>
        </div>

        <ImportColumnMapping
            :key="currentSection.key"
            :mapping="sectionMapping"
            :all-mapping="mapping"
            :providedColumns="providedColumns"
            :sampleRow="sampleRow"
            @update="onUpdate"
        />

        <v-alert v-if="missingRequired.length" type="error" variant="tonal" class="mt-4" density="compact">
            Campos requeridos sin mapear:
            <strong>{{ missingRequired.map(field => field.label).join(', ') }}</strong>
        </v-alert>

        <div class="d-flex justify-space-between mt-4 section_actions">
            <v-btn
                variant="outlined"
                color="indigo-darken-3"
                :text="activeSection === 0 ? 'Atrás' : 'Sección anterior'"
                :disabled="loading"
                @click="goBack"
            ></v-btn>
            <v-btn
                variant="flat"
                color="indigo-darken-3"
                :text="isLastSection ? 'Continuar' : 'Siguiente sección'"
                :disabled="loading || (isLastSection && missingRequired.length > 0)"
                :loading="loading"
                @click="goForward"
            ></v-btn>
        </div>
    </div>
</template>

<script>
    import ImportColumnMapping from '@/components/ImportColumnMapping.vue';

    export default {
        name: 'ImportMappingStep',
        components: {
            ImportColumnMapping,
        },
        data () {
            return {
                activeSection: 0,
                sections: [
                    {
                        key: 'general', title: 'General',
                        description: 'Identificación, asignación y datos generales del cliente.',
                        fields: ['CLIENTE_UNICO', 'NOMBRE_CTE', 'GESTORES', 'GERENCIA', 'PRODUCTO', 'ESTATUS_PROMESA_PAGO', 'RMD', 'CARTERA', 'EDAD_CLIENTE', 'GENERO_CLIENTE', 'TERRITORIO', 'ZONA', 'NOMBRE_DESPACHO', 'TELEFONO1', 'TELEFONO2', 'TELEFONO3', 'TELEFONO4'],
                    },
                    {
                        key: 'credito', title: 'Crédito',
                        description: 'Saldos, atrasos, último pago y datos de asignación.',
                        fields: ['ATRASO_MAXIMO', 'SALDO', 'MORATORIOS', 'SALDO_TOTAL', 'FIDIAPAGO', 'FECHA_ULTIMO_PAGO', 'IMP_ULTIMO_PAGO', 'CAMPANIA', 'FECHA_ASIGNACION', 'DIAS_ASIGNACION', 'ID GRUPO', 'GRUPO_MAZ'],
                    },
                    {
                        key: 'domicilios', title: 'Domicilio',
                        description: 'Domicilio particular y referencias del cliente.',
                        fields: ['DIRECCION_CTE', 'NUM_EXT_CTE', 'NUM_INT_CTE', 'CP_CTE', 'COLONIA_CTE', 'POBLACION_CTE', 'ESTADO_CTE', 'REFERENCIAS_DOMICILIO'],
                    },
                    {
                        key: 'trabajos', title: 'Trabajos',
                        description: 'Domicilio laboral del cliente.',
                        fields: ['CALLE_EMPLEO', 'NUM_EXT_EMPLEO', 'NUM_INT_EMPLEO', 'COLONIA_EMPLEO', 'POBLACION_EMPLEO', 'ESTADO_EMPLEO'],
                    },
                    {
                        key: 'avales', title: 'Aval(es)',
                        description: 'Nombre, teléfono y domicilio del aval.',
                        fields: ['CALLE_AVAL', 'NUM_EXT_AVAL', 'CP_AVAL', 'COLONIA_AVAL', 'POBLACION_AVAL', 'ESTADO_AVAL', 'TEL_AVAL', 'NOMBRE_AVAL'],
                    },
                ],
            };
        },
        props: {
            configuredFields: { type: Object, default: () => ({}) },
            mapping: {
                type: Array,
                default: () => [],
            },
            providedColumns: {
                type: Array,
                default: () => [],
            },
            sampleRow: {
                type: Array,
                default: () => [],
            },
            loading: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            currentSection () {
                return this.sections[this.activeSection];
            },
            sectionMapping () {
                const byKey = new Map(this.mapping.map((field) => [field.key, field]));
                const configured = this.configuredFields[this.currentSection.key] || [];
                const visible = new Set(configured.map(entry => entry.key));
                const configuredAnywhere = new Set(Object.values(this.configuredFields).flat().map(entry => entry.key));
                const ordered = [
                    ...configured,
                    ...this.currentSection.fields.filter(key => !visible.has(key) && !configuredAnywhere.has(key)
                        && byKey.has(key))
                        .map(key => ({ key, label: key })),
                ];
                return ordered.map(entry => {
                    const field = byKey.get(entry.key);
                    return field ? { ...field, label: entry.label } : null;
                }).filter(Boolean);
            },
            isLastSection () {
                return this.activeSection === this.sections.length - 1;
            },
            missingRequired () {
                return this.mapping.filter((field) => {
                    return field.required && (field.providedIndex === null || field.providedIndex === undefined);
                });
            },
        },
        methods: {
            goBack () {
                if (this.activeSection > 0) {
                    this.activeSection -= 1;
                } else {
                    this.$emit('back');
                }
            },
            goForward () {
                if (!this.isLastSection) {
                    this.activeSection += 1;
                } else if (this.missingRequired.length === 0) {
                    this.$emit('continue');
                }
            },
            onUpdate (payload) {
                this.$emit('update', payload);
            },
        },
    };
</script>

<style scoped>
    .mapping_step {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        min-width: 0;
    }

    .section_tabs,
    .section_description,
    .section_actions {
        flex: none;
    }

    .section_description {
        padding: 16px 8px 20px;
    }

    .section_tabs {
        margin-bottom: 8px;
    }

    .section_summary,
    .section_hint {
        margin: 0;
        line-height: 1.5;
    }

    .section_summary {
        font-size: 14px;
        color: #303030;
    }

    .section_hint {
        margin-top: 6px;
        font-size: 12px;
        color: #666;
    }
</style>
